import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  NotificationContext,
  defaultFilters,
  type NotificationContextValue,
} from '@/notifications/NotificationContext';
import {
  isEmbeddedNotificationMessage,
  toNotificationPublishInput,
} from '@/notifications/NotificationEvents';
import {
  archiveNotifications,
  deleteNotification,
  getNotificationCategories,
  getNotifications,
  getUnreadNotificationCount,
  markNotificationsRead,
  markNotificationsUnread,
  notificationService,
} from '@/notifications/NotificationService';
import type {
  NotificationFilters,
  NotificationPublishInput,
  NotificationRecord,
  NotificationTransportEvent,
  NotificationTransportMode,
} from '@/notifications/NotificationTypes';

interface NotificationProviderProps {
  children: ReactNode;
}

function withFilteredDuplicates(
  current: NotificationRecord[],
  incoming: NotificationRecord,
): NotificationRecord[] {
  if (current.some((item) => item.id === incoming.id)) {
    return current;
  }

  const duplicate = current.find((item) => {
    return (
      item.title === incoming.title &&
      item.message === incoming.message &&
      item.productId === incoming.productId &&
      Math.abs(
        new Date(item.createdAt).getTime() -
          new Date(incoming.createdAt).getTime(),
      ) < 60_000
    );
  });

  if (duplicate) {
    return current;
  }

  return [incoming, ...current];
}

function applyEvent(
  current: NotificationRecord[],
  event: NotificationTransportEvent,
): NotificationRecord[] {
  if (event.type === 'created') {
    return withFilteredDuplicates(current, event.notification);
  }

  if (event.type === 'updated') {
    return current.map((item) =>
      item.id === event.notification.id ? event.notification : item,
    );
  }

  return current.filter((item) => item.id !== event.notification.id);
}

function applyFilters(
  notifications: NotificationRecord[],
  filters: NotificationFilters,
): NotificationRecord[] {
  return notifications.filter((notification) => {
    if (
      filters.category !== 'all' &&
      notification.category !== filters.category
    ) {
      return false;
    }

    if (
      filters.severity !== 'all' &&
      notification.severity !== filters.severity
    ) {
      return false;
    }

    if (filters.status !== 'all' && notification.status !== filters.status) {
      return false;
    }

    if (filters.search.trim()) {
      const query = filters.search.trim().toLowerCase();
      const inFields = [
        notification.title,
        notification.message,
        notification.productName,
      ]
        .join(' ')
        .toLowerCase();

      if (!inFields.includes(query)) {
        return false;
      }
    }

    return true;
  });
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [transportMode, setTransportMode] =
    useState<NotificationTransportMode>('rest');
  const [filters, setFiltersState] =
    useState<NotificationFilters>(defaultFilters);
  const [categories, setCategories] = useState<string[]>([]);

  const refresh = useCallback(async () => {
    setIsLoading(true);

    try {
      const [items, unread] = await Promise.all([
        getNotifications(),
        getUnreadNotificationCount(),
      ]);

      const normalized = items.map((item) => {
        if (item.read) {
          return item;
        }

        return {
          ...item,
          status: 'unread' as const,
        };
      });

      const readCount = normalized.filter((item) => item.read).length;
      const shouldAdjust = unread + readCount !== normalized.length;

      setNotifications(
        shouldAdjust
          ? normalized.map((item) => ({
              ...item,
              read: item.read,
              status: item.archived
                ? 'archived'
                : item.read
                  ? 'read'
                  : 'unread',
            }))
          : normalized,
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleTransportEvent = (event: NotificationTransportEvent) => {
      setNotifications((current) => applyEvent(current, event));
    };

    const handleWindowMessage = (event: MessageEvent) => {
      if (!isEmbeddedNotificationMessage(event.data)) {
        return;
      }

      const input = toNotificationPublishInput(event.data.payload);
      void notificationService.publish(input);
    };

    notificationService.subscribe(handleTransportEvent);
    window.addEventListener('message', handleWindowMessage);

    void (async () => {
      await notificationService.connect('sse');
      setIsConnected(notificationService.getConnected());
      setTransportMode(notificationService.getTransportMode());
      setCategories(await getNotificationCategories());
      await refresh();
    })();

    return () => {
      notificationService.unsubscribe(handleTransportEvent);
      window.removeEventListener('message', handleWindowMessage);
      void notificationService.disconnect();
      setIsConnected(false);
    };
  }, [refresh]);

  const setFilters = useCallback((updates: Partial<NotificationFilters>) => {
    setFiltersState((current) => ({
      ...current,
      ...updates,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters);
  }, []);

  const markManyRead = useCallback(async (ids: string[]) => {
    if (!ids.length) {
      return;
    }

    setNotifications((current) =>
      current.map((item) =>
        ids.includes(item.id)
          ? { ...item, read: true, status: item.archived ? 'archived' : 'read' }
          : item,
      ),
    );

    await markNotificationsRead(ids);
  }, []);

  const markRead = useCallback(
    async (id: string) => {
      await markManyRead([id]);
    },
    [markManyRead],
  );

  const markUnread = useCallback(async (id: string) => {
    setNotifications((current) =>
      current.map((item) =>
        item.id === id
          ? {
              ...item,
              read: false,
              archived: false,
              status: 'unread',
            }
          : item,
      ),
    );

    await markNotificationsUnread([id]);
  }, []);

  const archiveMany = useCallback(async (ids: string[]) => {
    if (!ids.length) {
      return;
    }

    setNotifications((current) =>
      current.map((item) =>
        ids.includes(item.id)
          ? {
              ...item,
              archived: true,
              read: true,
              status: 'archived',
            }
          : item,
      ),
    );

    await archiveNotifications(ids);
  }, []);

  const archive = useCallback(
    async (id: string) => {
      await archiveMany([id]);
    },
    [archiveMany],
  );

  const remove = useCallback(async (id: string) => {
    setNotifications((current) => current.filter((item) => item.id !== id));
    await deleteNotification(id);
  }, []);

  const deleteMany = useCallback(async (ids: string[]) => {
    if (!ids.length) {
      return;
    }

    setNotifications((current) =>
      current.filter((item) => !ids.includes(item.id)),
    );
    await Promise.all(ids.map((id) => deleteNotification(id)));
  }, []);

  const markAllRead = useCallback(async () => {
    const unreadIds = notifications
      .filter((item) => !item.read)
      .map((item) => item.id);
    await markManyRead(unreadIds);
  }, [markManyRead, notifications]);

  const publish = useCallback(async (input: NotificationPublishInput) => {
    await notificationService.publish(input);
  }, []);

  const connect = useCallback(
    async (mode: NotificationTransportMode = 'sse') => {
      await notificationService.connect(mode);
      setIsConnected(notificationService.getConnected());
      setTransportMode(notificationService.getTransportMode());
    },
    [],
  );

  const disconnect = useCallback(async () => {
    await notificationService.disconnect();
    setIsConnected(notificationService.getConnected());
  }, []);

  const visibleNotifications = useMemo(
    () => applyFilters(notifications, filters),
    [filters, notifications],
  );

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read && !item.archived).length,
    [notifications],
  );

  const value: NotificationContextValue = useMemo(
    () => ({
      notifications: visibleNotifications,
      unreadCount,
      isLoading,
      isConnected,
      transportMode,
      filters,
      categories: categories as NotificationContextValue['categories'],
      refresh,
      setFilters,
      resetFilters,
      markRead,
      markUnread,
      archive,
      remove,
      markAllRead,
      markManyRead,
      archiveMany,
      deleteMany,
      publish,
      connect,
      disconnect,
    }),
    [
      archive,
      archiveMany,
      categories,
      connect,
      deleteMany,
      disconnect,
      filters,
      isConnected,
      isLoading,
      markAllRead,
      markManyRead,
      markRead,
      markUnread,
      publish,
      refresh,
      remove,
      resetFilters,
      setFilters,
      transportMode,
      unreadCount,
      visibleNotifications,
    ],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
